import { IconButton, Modal, TableRow } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ReserveProductTableColumns } from "../../configs/productOrder";
import CustomTable from "../../shared/components/CustomTable";
import { StyledTableCell } from "../../shared/components/CustomTable/Styled";
import SearchBarWithSuggestion from "../../shared/components/SearchBar/SearchBarWithSuggestion";
import TextBox from "../../shared/components/TextBox";
import DeleteIcon from "../../shared/svg/DeleteIcon";
import { StyledButton } from "../../shared/styled/TitleBar";
import { color } from "../../shared/styles";
import { api } from "../../shared/hooks/api";
import {
    ContentWrapper,
    ProductButtonRow,
    ProductTableText,
} from "../ProductOrder/single/Styled";
import {
    ButtonRow,
    Expandable,
    InfoText,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
    TitleWrapper,
} from "../ProductOrder/single/AddProductModal/Styled";
import { getWorkshopCategories } from "./utils";

const normalizeQuantity = (value: string | number) => {
    const quantity = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
};

const ReserveProductRow = ({ item, extraRowProps }: any) => (
    <TableRow>
        <StyledTableCell align="left">{item.product_code || item.code || "—"}</StyledTableCell>
        <StyledTableCell align="left">{item.name}</StyledTableCell>
        <StyledTableCell align="left">{item.category || "—"}</StyledTableCell>
        <StyledTableCell align="left">
            {extraRowProps.isView ? (
                item.selectedQuantity
            ) : (
                <TextBox
                    numeric
                    text={String(item.selectedQuantity || "")}
                    onChangeText={(value: string) =>
                        extraRowProps.updateQuantity(item.id, normalizeQuantity(value))
                    }
                    style={{ width: "150px" }}
                />
            )}
        </StyledTableCell>
        <StyledTableCell align="left">
            {!extraRowProps.isView && (
                <IconButton
                    aria-label={`Xóa ${item.name}`}
                    onClick={() => extraRowProps.removeProduct(item.id)}
                >
                    <DeleteIcon />
                </IconButton>
            )}
        </StyledTableCell>
    </TableRow>
);

const ReserveProductModal = ({
    open,
    workshop,
    selectedProducts,
    onClose,
    onSave,
}: any) => {
    const [isOpen, setIsOpen] = useState(open);
    const [selected, setSelected] = useState<any[]>(selectedProducts);
    const [searchItems, setSearchItems] = useState<any[]>([]);
    const categories = useMemo(
        () => getWorkshopCategories(workshop),
        [workshop],
    );
    const productsApiUrl = `/products?is_active=true&page_size=${Number.MAX_SAFE_INTEGER}&category=${categories.join(",")}`;
    const [{ data }, fetchProducts] = api.get(productsApiUrl, undefined, true);

    useEffect(() => {
        setSelected(selectedProducts);
    }, [selectedProducts, open]);

    useEffect(() => {
        const results = Array.isArray((data as any)?.results)
            ? (data as any).results
            : Array.isArray(data)
              ? data
              : [];
        setSearchItems(
            results.filter((product: any) => categories.includes(product.category)),
        );
    }, [data, categories]);

    useEffect(() => {
        if (open) setIsOpen(true);
    }, [open]);

    const handleClose = () => {
        setIsOpen(false);
        window.setTimeout(onClose, 200);
    };

    const handleToggle = (item: any, isAdded: boolean) => {
        if (isAdded && !selected.some((product) => product.id === item.id)) {
            setSelected([
                ...selected,
                { ...item, item_type: "product", selectedQuantity: 1 },
            ]);
        } else if (!isAdded) {
            setSelected(selected.filter((product) => product.id !== item.id));
        }
    };

    const updateQuantity = (id: string, quantity: number) => {
        setSelected(
            selected.map((product) =>
                product.id === id
                    ? { ...product, selectedQuantity: quantity }
                    : product,
            ),
        );
    };

    const removeProduct = (id: string) => {
        setSelected(selected.filter((product) => product.id !== id));
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
        >
            <ModalWrapper isOpen={isOpen}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>Thêm sản phẩm dự phòng</TitleText>
                        <InfoText>
                            Chỉ hiển thị sản phẩm thuộc {categories.join(", ")}
                        </InfoText>
                    </TitleWrapper>
                    <SearchBarWithSuggestion
                        menuList={searchItems}
                        selectedItems={selected}
                        fetchItems={fetchProducts}
                        handleOnclickItem={handleToggle}
                        placeholder="Tìm kiếm theo mã hoặc tên sản phẩm"
                    />
                    <CustomTable
                        itemList={selected}
                        RowElement={ReserveProductRow}
                        TableColumns={ReserveProductTableColumns}
                        extraRowProps={{ updateQuantity, removeProduct }}
                        containerStyle={{ maxHeight: "400px" }}
                    />
                </Expandable>
                <ButtonRow>
                    <StyledButton onClick={handleClose}>Huỷ bỏ</StyledButton>
                    <StyledButton
                        variant="contained"
                        onClick={() => {
                            onSave(selected);
                            handleClose();
                        }}
                    >
                        Lưu
                    </StyledButton>
                </ButtonRow>
            </ModalWrapper>
        </Modal>
    );
};

interface ReserveProductListProps {
    products?: any[];
    workshop?: string | null;
    isView?: boolean;
    onChange?: (products: any[]) => void;
}

const ReserveProductList = ({
    products = [],
    workshop,
    isView = false,
    onChange = () => undefined,
}: ReserveProductListProps) => {
    const [isModalOpen, setModalOpen] = useState(false);

    const updateQuantity = (id: string, quantity: number) => {
        onChange(
            products.map((product) =>
                product.id === id
                    ? { ...product, selectedQuantity: quantity }
                    : product,
            ),
        );
    };

    const removeProduct = (id: string) => {
        onChange(products.filter((product) => product.id !== id));
    };

    return (
        <>
            {isModalOpen && !isView && (
                <ReserveProductModal
                    open={isModalOpen}
                    workshop={workshop}
                    selectedProducts={products}
                    onClose={() => setModalOpen(false)}
                    onSave={onChange}
                />
            )}
            <ContentWrapper>
                <ProductTableText>
                    Danh sách sản phẩm{" "}
                    <span
                        style={{
                            color: color.secondary.DARK_GREY,
                            fontSize: "16px",
                            fontWeight: 400,
                        }}
                    >
                        ({products.length} sản phẩm)
                    </span>
                </ProductTableText>
                <CustomTable
                    itemList={products}
                    RowElement={ReserveProductRow}
                    TableColumns={ReserveProductTableColumns}
                    extraRowProps={{
                        isView,
                        updateQuantity,
                        removeProduct,
                    }}
                    containerStyle={{ maxHeight: "400px" }}
                />
                {products.length === 0 && "Không có sản phẩm được chọn"}
                {!isView && (
                    <ProductButtonRow>
                        <StyledButton
                            variant="contained"
                            disabled={!workshop}
                            onClick={() => setModalOpen(true)}
                        >
                            Thêm sản phẩm
                        </StyledButton>
                    </ProductButtonRow>
                )}
            </ContentWrapper>
        </>
    );
};

export default ReserveProductList;
